import { HttpErrorResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnDestroy, OnInit, TemplateRef, computed, inject, signal, viewChild } from '@angular/core';
import { Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AlertService,
  AlertType,
  ButtonType,
  ColumnConfig,
  ConfirmationDialogService,
  CustomAction,
  DynamicFieldDirective,
  FieldType,
  GenericModalComponent,
  GenericTableComponent,
  GlobalAction,
  HeaderService,
  LoaderService,
  ModalFieldConfig,
  ModalMode,
  RowStyleConfig,
} from '@solcre-org/core-ui';
import { finalize } from 'rxjs';
import { ApiResponseModel, ApiService } from '@solcre-org/core';
import { PortTariffLineUnitTypes, isCargoUnitType } from '../../../shared/enums/port-tariff-line-unit-types.enum';
import { PortTariffLineVesselProperties } from '../../../shared/enums/port-tariff-line-vessel-properties.enum';
import { PortTariffLineTypes } from '../../../shared/enums/port-tariff-line-types.enum';
import { PortTariffLineExpenseCategories } from '../../../shared/enums/port-tariff-line-expense-category.enum';
import { PortTariffLineOperationDirections } from '../../../shared/enums/port-tariff-line-operation-direction.enum';
import { PortTariffLineFormModel, TariffLineKind } from '../../../shared/models/port-tariff-line-form.model';
import { PortTariffLineModel } from '../../../shared/models/port-tariff-line.model';
import { PortTariffModel } from '../../../shared/models/port-tariff.model';
import { CargoTypeModel } from '../../../shared/models/cargo-type.model';
import { VesselModel } from '../../../shared/models/vessel.model';
import { cargoTypeUriTemplate } from '../../../shared/templates/cargoTypeUriTemplate';
import { vesselUriTemplate } from '../../../shared/templates/vesselUriTemplate';
import { environment } from '../../../../environments/environment.local';
import { PortTariffDisplayService } from '../../../shared/services/port-tariff-display.service';
import { ALL_CARGO_TYPES_VALUE, ALL_VESSELS_VALUE, PortTariffLineFormService } from './port-tariff-line-form.service';
import { PortTariffService } from '../../../shared/services/port-tariff.service';

type PortTariffLinesContext = {
  portCode: string;
  portName: string;
  terminal: string;
  country: string;
  berthingType: string;
  tariffId: string;
};

@Component({
  selector: 'app-port-tariff-lines',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslateModule,
    GenericTableComponent,
    GenericModalComponent,
    DynamicFieldDirective,
  ],
  templateUrl: './port-tariff-lines.html',
  styleUrls: ['./port-tariff-lines.css'],
})
export class PortTariffLines implements OnInit, OnDestroy {
  readonly LineType = PortTariffLineTypes;
  readonly AllCargoTypesValue = ALL_CARGO_TYPES_VALUE;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly headerService = inject(HeaderService);
  private readonly loaderService = inject(LoaderService);
  private readonly alertService = inject(AlertService);
  private readonly confirmService = inject(ConfirmationDialogService);
  private readonly tariffService = inject(PortTariffService);
  private readonly translateService = inject(TranslateService);
  private readonly displayService = inject(PortTariffDisplayService);
  private readonly lineFormService = inject(PortTariffLineFormService);
  private readonly apiService = inject(ApiService);

  statusCellTemplate = viewChild<TemplateRef<{ $implicit: PortTariffLineModel; column: ColumnConfig<PortTariffLineModel> }>>('statusCellTemplate');
  formulaPreviewCellTemplate = viewChild<TemplateRef<{ $implicit: PortTariffLineModel; column: ColumnConfig<PortTariffLineModel> }>>('formulaPreviewCellTemplate');

  portId = signal<string>('');
  tariffId = signal<string>('');
  tariff = signal<PortTariffModel | null>(null);
  context = signal<PortTariffLinesContext>({
    portCode: '',
    portName: '',
    terminal: '',
    country: '',
    berthingType: '',
    tariffId: '',
  });
  isLoading = signal(false);
  cargoTypes = signal<CargoTypeModel[]>([]);
  vessels = signal<VesselModel[]>([]);

  lineFormOpen = signal(false);
  lineModalMode = signal<ModalMode>(ModalMode.CREATE);
  editingLineId = signal<string | null>(null);
  // Plain property — GenericModalComponent clones the data on open; mutations here
  // after that have no effect on the live form.
  lineDraft: PortTariffLineFormModel = this.lineFormService.createEmptyDraft();

  lineColumns = computed<ColumnConfig<PortTariffLineModel>[]>(() => [
    {
      label: '#',
      key: 'sortOrder',
      align: 'center',
    },
    {
      label: this.t('portTariffLines.table.concept'),
      key: 'concept',
    },
    {
      label: this.t('portTariffLines.table.type'),
      key: 'lineType',
      format: (value) => this.displayService.lineTypeLabel(value),
    },
    {
      label: this.t('portTariffLines.table.rate'),
      key: 'rateValue',
      format: (_value, row) => this.blockDisplayRate(row.lineType, row.unitType, row.rateValue),
    },
    {
      label: this.t('portTariffLines.table.ot1Rate'),
      key: 'ot1RateValue',
      format: (_value, row) => this.blockDisplayRate(row.lineType, row.ot1UnitType, row.ot1RateValue),
    },
    {
      label: this.t('portTariffLines.table.ot2Rate'),
      key: 'ot2RateValue',
      format: (_value, row) => this.blockDisplayRate(row.lineType, row.ot2UnitType, row.ot2RateValue),
    },
    {
      label: this.t('portTariffLines.table.unit'),
      key: 'unitType',
      format: (value) => this.displayService.unitTypeLabel(value),
    },
    {
      label: this.t('portTariffLines.table.vesselProperty'),
      key: 'vesselProperty',
      format: (value) => this.displayService.vesselPropertyLabel(value),
    },
    {
      label: this.t('portTariffLines.table.applicableVessel'),
      key: 'applicableVessel',
      format: (value) => this.vesselName(value),
    },
    {
      label: this.t('portTariffLines.table.expenseCategory'),
      key: 'expenseCategory',
      format: (value) => this.displayService.expenseCategoryLabel(value),
    },
    {
      label: this.t('portTariffLines.table.formulaPreview'),
      key: 'formulaPreview',
      template: this.formulaPreviewCellTemplate(),
    },
    {
      label: this.t('portTariffLines.table.status'),
      key: 'status',
      template: this.statusCellTemplate(),
    },
  ]);

  lineRowStyleConfigs = computed<RowStyleConfig<PortTariffLineModel>[]>(() => [
    {
      condition: (row) => !this.isActiveLine(row),
      className: 'port-tariff-lines-table__row--archived',
      priority: 1,
    },
  ]);

  lineGlobalActions = computed<GlobalAction<PortTariffLineModel>[]>(() => [
    {
      action: 'backToTariffs',
      label: this.t('portTariffLines.actions.backToTariffs'),
      icon: 'icon-arrow-left',
      callback: () => this.goBackToTariffs(),
      class: 'c-btn c-btn--stroked',
    },
    {
      action: 'addLine',
      label: this.t('portTariffLines.actions.addLine'),
      icon: 'icon-add',
      callback: () => this.openCreateLine(),
      class: 'c-btn',
    },
  ]);

  lineCustomActions = computed<CustomAction<PortTariffLineModel>[]>(() => [
    {
      title: this.t('portTariffLines.actions.editLine'),
      icon: 'icon-edit',
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'icon-edit',
      },
      isExtra: false,
      tooltipPosition: 'top',
      shouldShow: (line) => this.isActiveLine(line) && this.isEditableTariff(),
      callback: (line) => this.openEditLine(line),
    },
    {
      title: this.t('portTariffLines.actions.moveLineUp'),
      icon: 'fa-regular fa-circle-up',
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'fa-regular fa-circle-up',
      },
      isExtra: false,
      tooltipPosition: 'top',
      shouldShow: (line) => this.isActiveLine(line) && this.isEditableTariff(),
      shouldDisable: (line) => !this.canMoveLineUp(line),
      callback: (line) => this.moveLine(line, -1),
    },
    {
      title: this.t('portTariffLines.actions.moveLineDown'),
      icon: 'fa-regular fa-circle-down',
      buttonConfig: {
        type: ButtonType.ICON,
        icon: 'fa-regular fa-circle-down',
      },
      isExtra: false,
      tooltipPosition: 'top',
      shouldShow: (line) => this.isActiveLine(line) && this.isEditableTariff(),
      shouldDisable: (line) => !this.canMoveLineDown(line),
      callback: (line) => this.moveLine(line, 1),
    },
    {
      title: this.t('portTariffLines.actions.archiveLine'),
      icon: 'fa-regular fa-box-archive',
      class: 'context:error',
      isExtra: true,
      shouldShow: (line) => this.isActiveLine(line) && this.isEditableTariff(),
      callback: (line) => this.archiveLine(line),
    },
  ]);

  conceptFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'concept',
    label: this.t('portTariffLines.fields.concept'),
    placeholder: this.t('portTariffLines.fields.conceptPlaceholder'),
    type: FieldType.TEXT,
    readonly: false,
    validators: [Validators.required, Validators.maxLength(255)],
    errorMessages: {
      required: this.t('validators.required'),
      maxlength: this.t('portTariffLines.validation.conceptMaxLength'),
    } as Record<string, string>,
  }));

  // Empty is a valid, meaningful state (this block simply doesn't apply) — only the
  // pattern is enforced, and Validators.pattern doesn't fire on an empty string.
  rateValueFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'rateValue',
    label: '',
    placeholder: this.t('portTariffLines.fields.rateValuePlaceholder'),
    type: FieldType.TEXT,
    readonly: false,
    validators: [Validators.pattern(/^\d+(\.\d{1,4})?$/)],
    errorMessages: {
      pattern: this.t('portTariffLines.validation.rateValuePattern'),
    } as Record<string, string>,
  }));

  quantityMultiplierFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'quantityMultiplier',
    label: '',
    placeholder: this.t('portTariffLines.fields.quantityMultiplierPlaceholder'),
    type: FieldType.TEXT,
    // Tax lines always multiply by 1 — locked instead of hidden so it's clear it's still applied last.
    readonly: (formValue: PortTariffLineFormModel) => formValue?.lineType === PortTariffLineTypes.TAX,
    validators: [Validators.pattern(/^\d+(\.\d{1,4})?$/)],
    errorMessages: {
      pattern: this.t('portTariffLines.validation.quantityMultiplierPattern'),
    } as Record<string, string>,
  }));

  unitTypeFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'unitType',
    label: '',
    type: FieldType.SELECT,
    // Locks the select once PCT is chosen — to switch away the user must first
    // go back to Standard (setLineType), which resets to FIXED automatically.
    readonly: (formValue: PortTariffLineFormModel) => formValue?.unitType === PortTariffLineUnitTypes.PCT,
    dynamicOptions: (row: PortTariffLineFormModel) => this.lineFormService.unitTypeOptions(row.lineType),
    validators: [],
  }));

  vesselPropertyFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'vesselProperty',
    label: this.t('portTariffLines.fields.vesselProperty'),
    type: FieldType.SELECT,
    readonly: false,
    dynamicOptions: (row: PortTariffLineFormModel) => this.lineFormService.vesselPropertyOptions(row.unitType),
    validators: [],
  }));

  expenseCategoryFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'expenseCategory',
    label: this.t('portTariffLines.fields.expenseCategory'),
    type: FieldType.SELECT,
    readonly: false,
    options: this.lineFormService.expenseCategoryOptions(),
    validators: [Validators.required],
  }));

  operationDirectionFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'operationDirection',
    label: this.t('portTariffLines.fields.operationDirection'),
    type: FieldType.SELECT,
    readonly: false,
    options: this.lineFormService.operationDirectionOptions(),
    validators: [Validators.required],
  }));

  cargoTypeFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'cargoType',
    label: this.t('portTariffLines.fields.cargoType'),
    type: FieldType.SELECT,
    readonly: false,
    options: [
      { value: ALL_CARGO_TYPES_VALUE, label: this.t('portTariffLines.options.cargoType.allCargoUnits') },
      ...this.cargoTypes().map((cargoType) => ({ value: cargoType.id, label: cargoType.name ?? '' })),
    ],
    validators: [],
  }));

  ot1CargoTypeFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot1CargoType',
    label: this.t('portTariffLines.fields.cargoType'),
    type: FieldType.SELECT,
    readonly: false,
    options: [
      { value: ALL_CARGO_TYPES_VALUE, label: this.t('portTariffLines.options.cargoType.allCargoUnits') },
      ...this.cargoTypes().map((cargoType) => ({ value: cargoType.id, label: cargoType.name ?? '' })),
    ],
    validators: [],
  }));

  ot2CargoTypeFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot2CargoType',
    label: this.t('portTariffLines.fields.cargoType'),
    type: FieldType.SELECT,
    readonly: false,
    options: [
      { value: ALL_CARGO_TYPES_VALUE, label: this.t('portTariffLines.options.cargoType.allCargoUnits') },
      ...this.cargoTypes().map((cargoType) => ({ value: cargoType.id, label: cargoType.name ?? '' })),
    ],
    validators: [],
  }));

  // core-ui's select field can't represent `null` as a real selection (see ALL_CARGO_TYPES_VALUE
  // comment) — translate to/from the sentinel at the template boundary only.
  cargoTypeSelectValue(cargoType: string | null | undefined): string {
    return cargoType ?? ALL_CARGO_TYPES_VALUE;
  }

  onCargoTypeChange(updateField: (key: string, value: unknown) => void, key: string, value: string): void {
    updateField(key, value === ALL_CARGO_TYPES_VALUE ? null : value);
  }

  // Shared across the whole line — not per-block — so it lives outside the accordion.
  applicableVesselFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'applicableVessel',
    label: this.t('portTariffLines.fields.applicableVessel'),
    type: FieldType.SELECT,
    readonly: false,
    options: [
      { value: ALL_VESSELS_VALUE, label: this.t('portTariffLines.options.applicableVessel.allVessels') },
      ...this.vessels().map((vessel) => ({ value: vessel.id, label: vessel.vesselName ?? '' })),
    ],
    validators: [],
  }));

  // Same null/sentinel translation as cargoTypeSelectValue, for the vessel selector.
  applicableVesselSelectValue(applicableVessel: string | null | undefined): string {
    return applicableVessel ?? ALL_VESSELS_VALUE;
  }

  onApplicableVesselChange(updateField: (key: string, value: unknown) => void, value: string): void {
    updateField('applicableVessel', value === ALL_VESSELS_VALUE ? null : value);
  }

  // --- OT1 block (mirrors the Standard block fields above) ---

  ot1RateValueFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot1RateValue',
    label: '',
    placeholder: this.t('portTariffLines.fields.rateValuePlaceholder'),
    type: FieldType.TEXT,
    readonly: false,
    validators: [Validators.pattern(/^\d+(\.\d{1,4})?$/)],
    errorMessages: {
      pattern: this.t('portTariffLines.validation.rateValuePattern'),
    } as Record<string, string>,
  }));

  ot1QuantityMultiplierFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot1QuantityMultiplier',
    label: '',
    placeholder: this.t('portTariffLines.fields.quantityMultiplierPlaceholder'),
    type: FieldType.TEXT,
    readonly: (formValue: PortTariffLineFormModel) => formValue?.lineType === PortTariffLineTypes.TAX,
    validators: [Validators.pattern(/^\d+(\.\d{1,4})?$/)],
    errorMessages: {
      pattern: this.t('portTariffLines.validation.quantityMultiplierPattern'),
    } as Record<string, string>,
  }));

  ot1UnitTypeFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot1UnitType',
    label: '',
    type: FieldType.SELECT,
    readonly: (formValue: PortTariffLineFormModel) => formValue?.ot1UnitType === PortTariffLineUnitTypes.PCT,
    dynamicOptions: (row: PortTariffLineFormModel) => this.lineFormService.unitTypeOptions(row.lineType),
    validators: [],
  }));

  ot1VesselPropertyFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot1VesselProperty',
    label: this.t('portTariffLines.fields.vesselProperty'),
    type: FieldType.SELECT,
    readonly: false,
    dynamicOptions: (row: PortTariffLineFormModel) => this.lineFormService.vesselPropertyOptions(row.ot1UnitType),
    validators: [],
  }));

  // --- OT2 block (mirrors the Standard block fields above) ---

  ot2RateValueFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot2RateValue',
    label: '',
    placeholder: this.t('portTariffLines.fields.rateValuePlaceholder'),
    type: FieldType.TEXT,
    readonly: false,
    validators: [Validators.pattern(/^\d+(\.\d{1,4})?$/)],
    errorMessages: {
      pattern: this.t('portTariffLines.validation.rateValuePattern'),
    } as Record<string, string>,
  }));

  ot2QuantityMultiplierFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot2QuantityMultiplier',
    label: '',
    placeholder: this.t('portTariffLines.fields.quantityMultiplierPlaceholder'),
    type: FieldType.TEXT,
    readonly: (formValue: PortTariffLineFormModel) => formValue?.lineType === PortTariffLineTypes.TAX,
    validators: [Validators.pattern(/^\d+(\.\d{1,4})?$/)],
    errorMessages: {
      pattern: this.t('portTariffLines.validation.quantityMultiplierPattern'),
    } as Record<string, string>,
  }));

  ot2UnitTypeFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot2UnitType',
    label: '',
    type: FieldType.SELECT,
    readonly: (formValue: PortTariffLineFormModel) => formValue?.ot2UnitType === PortTariffLineUnitTypes.PCT,
    dynamicOptions: (row: PortTariffLineFormModel) => this.lineFormService.unitTypeOptions(row.lineType),
    validators: [],
  }));

  ot2VesselPropertyFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'ot2VesselProperty',
    label: this.t('portTariffLines.fields.vesselProperty'),
    type: FieldType.SELECT,
    readonly: false,
    dynamicOptions: (row: PortTariffLineFormModel) => this.lineFormService.vesselPropertyOptions(row.ot2UnitType),
    validators: [],
  }));

  vesselName(applicableVessel: string | null | undefined): string {
    if (!applicableVessel) {
      return this.t('portTariffLines.options.applicableVessel.allVessels');
    }

    return this.vessels().find((vessel) => vessel.id === applicableVessel)?.vesselName ?? '';
  }

  // Rendered manually as radio cards in the custom template, but still needs to be
  // registered here — core-generic-modal's updateField() no-ops for keys outside [fields].
  lineTypeFieldConfig = computed<ModalFieldConfig<PortTariffLineFormModel>>(() => ({
    key: 'lineType',
    label: this.t('portTariffLines.fields.lineType'),
    type: FieldType.SELECT,
    readonly: false,
    options: [
      { value: PortTariffLineTypes.STANDARD, label: this.t('portTariffLines.options.lineType.standard') },
      { value: PortTariffLineTypes.TAX, label: this.t('portTariffLines.options.lineType.tax') },
    ],
    validators: [Validators.required],
  }));

  // [fields] must be provided alongside [customTemplate]: GenericModalComponent's
  // updateField() looks up keys in allFields() and silently no-ops if the key isn't there.
  // The custom template handles rendering; this array only registers the keys.
  lineModalFields = computed<ModalFieldConfig<PortTariffLineFormModel>[]>(() => [
    this.conceptFieldConfig(),
    this.lineTypeFieldConfig(),
    this.rateValueFieldConfig(),
    this.quantityMultiplierFieldConfig(),
    this.unitTypeFieldConfig(),
    this.vesselPropertyFieldConfig(),
    this.cargoTypeFieldConfig(),
    this.applicableVesselFieldConfig(),
    this.ot1RateValueFieldConfig(),
    this.ot1QuantityMultiplierFieldConfig(),
    this.ot1UnitTypeFieldConfig(),
    this.ot1VesselPropertyFieldConfig(),
    this.ot1CargoTypeFieldConfig(),
    this.ot2RateValueFieldConfig(),
    this.ot2QuantityMultiplierFieldConfig(),
    this.ot2UnitTypeFieldConfig(),
    this.ot2VesselPropertyFieldConfig(),
    this.ot2CargoTypeFieldConfig(),
    this.expenseCategoryFieldConfig(),
    this.operationDirectionFieldConfig(),
  ]);

  // Accordion open/closed state for the three rate blocks — Standard starts expanded,
  // OT1/OT2 collapsed since most lines never touch them.
  openBlocks = signal<{ standard: boolean; ot1: boolean; ot2: boolean }>({ standard: true, ot1: false, ot2: false });

  toggleBlock(block: 'standard' | 'ot1' | 'ot2'): void {
    this.openBlocks.update((current) => ({ ...current, [block]: !current[block] }));
  }

  tariffLines = computed(() => this.sortedLines(this.tariff()?.lines ?? []));
  headerTitle = computed(() => {
    const context = this.context();
    return context.portName || context.portCode || this.t('portTariffLines.header.fallbackTitle', { portId: this.portId() || '' });
  });
  headerMeta = computed(() => {
    const context = this.context();
    return [context.portCode, context.country, context.terminal, this.displayService.berthingTypeLabel(context.berthingType)]
      .filter(Boolean)
      .join(' · ') || this.t('portTariffLines.header.fallbackMeta');
  });

  ngOnInit(): void {
    this.apiService.fetchData(environment.API.CARGO_TYPES_URI).subscribe({
      next: (response: ApiResponseModel) => {
        this.cargoTypes.set(
          new CargoTypeModel().fromJSONList(this.extractCollection(response.data)).map((cargoType) => {
            cargoType.id = cargoTypeUriTemplate(cargoType.id);
            return cargoType;
          })
        );
      },
    });

    this.apiService.fetchData(environment.API.VESSELS_URI).subscribe({
      next: (response: ApiResponseModel) => {
        this.vessels.set(
          new VesselModel().fromJSONList(this.extractCollection(response.data)).map((vessel) => {
            vessel.id = vesselUriTemplate(vessel.id);
            return vessel;
          })
        );
      },
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.portId.set(params.get('id') ?? '');
      this.tariffId.set(params.get('tariffId') ?? '');
      this.reloadData();
    });

    // Display metadata (portName, berthingType…) arrives as query params — no extra GET.
    // Fires independently from paramMap, so updateHeaderConfig() is called in both handlers.
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.context.set({
        portCode: params.get('portCode') ?? '',
        portName: params.get('portName') ?? '',
        terminal: params.get('terminal') ?? '',
        country: params.get('country') ?? '',
        berthingType: params.get('berthingType') ?? '',
        tariffId: params.get('tariffId') ?? '',
      });
      this.updateHeaderConfig();
    });
  }

  reloadData(): void {
    const tariffId = this.tariffId();
    if (!tariffId) {
      this.tariff.set(null);
      this.updateHeaderConfig();
      return;
    }

    this.isLoading.set(true);
    this.loaderService.showLoader();
    this.tariffService.fetchPortTariff(tariffId).pipe(
      finalize(() => {
        this.isLoading.set(false);
        this.loaderService.hideLoader();
      })
    ).subscribe({
      next: (tariff) => {
        this.tariff.set(tariff);
        this.updateHeaderConfig();
      },
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  goBackToTariffs(): void {
    if (!this.portId()) {
      this.router.navigate(['/app/ports']);
      return;
    }

    const context = this.context();
    this.router.navigate(['/app/ports', this.portId(), 'tariffs'], {
      queryParams: {
        portCode: context.portCode,
        portName: context.portName,
        terminal: context.terminal,
        country: context.country,
        berthingType: context.berthingType,
      },
    });
  }

  openCreateLine(): void {
    if (!this.isEditableTariff()) {
      return;
    }

    this.editingLineId.set(null);
    this.lineDraft = this.lineFormService.createEmptyDraft({
      sortOrder: this.lineFormService.nextSortOrder(this.activeTariffLines()),
    });
    this.openBlocks.set({ standard: true, ot1: false, ot2: false });
    this.lineModalMode.set(ModalMode.CREATE);
    this.lineFormOpen.set(true);
  }

  openEditLine(line: PortTariffLineModel): void {
    if (!line || !this.isActiveLine(line) || !this.isEditableTariff()) {
      return;
    }

    this.editingLineId.set(String(line.id ?? ''));
    this.lineDraft = this.lineFormService.draftFromModel(line);
    this.openBlocks.set({
      standard: true,
      ot1: this.lineDraft.ot1RateValue !== null,
      ot2: this.lineDraft.ot2RateValue !== null,
    });
    this.lineModalMode.set(ModalMode.EDIT);
    this.lineFormOpen.set(true);
  }

  closeLineForm(): void {
    this.lineFormOpen.set(false);
  }

  // Tax / % implies PCT; auto-selecting it avoids a second required click.
  // vesselProperty is reset because tax lines never use a vessel multiplier.
  // Applied to all three blocks — lineType is shared by Standard/OT1/OT2.
  setLineType(updateField: (key: string, value: unknown) => void, kind: TariffLineKind): void {
    const unitType = kind === PortTariffLineTypes.TAX ? PortTariffLineUnitTypes.PCT : PortTariffLineUnitTypes.FIXED;
    updateField('lineType', kind);
    updateField('unitType', unitType);
    updateField('vesselProperty', PortTariffLineVesselProperties.NONE);
    updateField('ot1UnitType', unitType);
    updateField('ot1VesselProperty', PortTariffLineVesselProperties.NONE);
    updateField('ot2UnitType', unitType);
    updateField('ot2VesselProperty', PortTariffLineVesselProperties.NONE);

    if (kind === PortTariffLineTypes.TAX) {
      updateField('quantityMultiplier', '1.0000');
      updateField('ot1QuantityMultiplier', '1.0000');
      updateField('ot2QuantityMultiplier', '1.0000');
    }
  }

  // Resets vesselProperty to the first valid option for the new unit type to prevent
  // impossible combinations (e.g. LOA stuck when switching from meter to ton).
  setUnitType(updateField: (key: string, value: unknown) => void, unitType: string): void {
    this.applyBlockUnitType(updateField, unitType, 'unitType', 'vesselProperty', 'cargoType');
  }

  setOt1UnitType(updateField: (key: string, value: unknown) => void, unitType: string): void {
    this.applyBlockUnitType(updateField, unitType, 'ot1UnitType', 'ot1VesselProperty', 'ot1CargoType');
  }

  setOt2UnitType(updateField: (key: string, value: unknown) => void, unitType: string): void {
    this.applyBlockUnitType(updateField, unitType, 'ot2UnitType', 'ot2VesselProperty', 'ot2CargoType');
  }

  private applyBlockUnitType(
    updateField: (key: string, value: unknown) => void,
    unitType: string,
    unitTypeKey: string,
    vesselPropertyKey: string,
    cargoTypeKey: string
  ): void {
    updateField(unitTypeKey, unitType);
    const options = this.lineFormService.vesselPropertyOptions(unitType);
    updateField(vesselPropertyKey, options[0]?.value ?? PortTariffLineVesselProperties.NONE);

    if (!isCargoUnitType(unitType)) {
      updateField(cargoTypeKey, null);
    }
  }

  // Copies Standard's whole formula (unit, vessel property, cargo type, applicable
  // vessel, multiplier and rate) into an OT block — most overtime lines only change
  // the amount, so this saves re-picking every selector by hand.
  copyStandardToBlock(updateField: (key: string, value: unknown) => void, data: PortTariffLineFormModel, target: 'ot1' | 'ot2'): void {
    const prefix = target;
    updateField(`${prefix}RateValue`, data.rateValue);
    updateField(`${prefix}UnitType`, data.unitType);
    updateField(`${prefix}VesselProperty`, data.vesselProperty);
    updateField(`${prefix}CargoType`, data.cargoType);
    updateField(`${prefix}QuantityMultiplier`, data.quantityMultiplier);
    this.openBlocks.update((current) => ({ ...current, [target]: true }));
  }

  needsCargoTypeSelector(unitType: string): boolean {
    return isCargoUnitType(unitType);
  }

  // Tax lines (fixed or PCT) have no vessel multiplier. Per-cargo-unit standard lines
  // (unit/CBM/MT/W-M) don't need one either, so vesselProperty stays NONE for those.
  // Fixed amount lines DO support an optional vessel property multiplier (e.g. 100 x GRT).
  needsVesselPropertySelector(lineType: string, unitType: string): boolean {
    return lineType !== PortTariffLineTypes.TAX && !isCargoUnitType(unitType);
  }

  // Direction only affects cargo-basis stevedoring lines (loading/discharge/both quantity).
  // Every other category is forced to BOTH, so the selector is hidden for them.
  needsOperationDirectionSelector(expenseCategory: string): boolean {
    return expenseCategory === PortTariffLineExpenseCategories.STEVEDORING;
  }

  // Non-stevedoring categories always store BOTH ("whole scale") since the
  // direction field is hidden for them and there's no dedicated UI to set it.
  onExpenseCategoryChange(updateField: (key: string, value: unknown) => void, value: string): void {
    updateField('expenseCategory', value);
    if (value !== PortTariffLineExpenseCategories.STEVEDORING) {
      updateField('operationDirection', PortTariffLineOperationDirections.BOTH);
    }
  }

  isPercentRate(lineType: string, unitType: string): boolean {
    return lineType === PortTariffLineTypes.TAX && unitType === PortTariffLineUnitTypes.PCT;
  }

  formulaTitleKey(lineType: TariffLineKind | string): string {
    return lineType === PortTariffLineTypes.TAX ? 'portTariffLines.formula.tax' : 'portTariffLines.formula.standard';
  }

  // Form context: an empty rate means the block doesn't apply — show a placeholder
  // instead of formatting it as if it were priced at USD 0. PCT rateValue is stored as
  // % (50 for 50%), but displayService expects the raw decimal (0.5) — pre-convert.
  blockPreview(lineType: string, unitType: string, vesselProperty: string, rateValue: string | null, quantityMultiplier: string): string {
    if (rateValue === null || rateValue === undefined || String(rateValue).trim() === '') {
      return this.t('portTariffLines.formula.noRate');
    }

    const rate = this.isPercentRate(lineType, unitType) ? String(Number(rateValue) / 100) : rateValue;
    return this.displayService.linePreview({ lineType, unitType, vesselProperty, rateValue: rate, quantityMultiplier });
  }

  // Table context: rateValue comes from backend as decimal (0.5 for 50%). No conversion needed.
  tableLinePreview(line: PortTariffLineModel): string {
    return this.displayService.linePreview(line);
  }

  // '-' distinguishes an unset block from one priced at USD 0.
  blockDisplayRate(lineType: string | undefined, unitType: string | null | undefined, rateValue: string | null | undefined): string {
    if (rateValue === null || rateValue === undefined || String(rateValue).trim() === '') {
      return '-';
    }

    return this.displayService.lineDisplayRate({ lineType: lineType ?? '', unitType: unitType ?? '', rateValue });
  }

  onLineModalSave(formValue: PortTariffLineFormModel): void {
    const tariff = this.tariff();
    if (!tariff?.id || !this.isEditableTariff()) {
      return;
    }

    // Custom template bypasses GenericModal's per-field validation, so we run
    // our own business-rule check here before sending the request.
    const validationError = this.lineFormService.validate(formValue);
    if (validationError) {
      this.showMessage(validationError, AlertType.DEFAULT);
      return;
    }

    const payload = this.lineFormService.buildPayload(formValue);
    const request = this.editingLineId()
      ? this.tariffService.updatePortTariffLine(String(this.editingLineId()), payload)
      : this.tariffService.createPortTariffLine(tariff.id, payload);

    this.loaderService.showLoader();
    request.pipe(finalize(() => this.loaderService.hideLoader())).subscribe({
      next: () => {
        this.showMessage(
          this.editingLineId() ? this.t('portTariffLines.messages.lineUpdated') : this.t('portTariffLines.messages.lineCreated'),
          AlertType.SUCCESS
        );
        this.closeLineForm();
        this.reloadData();
      },
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  archiveLine(line: PortTariffLineModel): void {
    if (!line?.id || !this.isActiveLine(line)) {
      return;
    }

    this.confirmService
      .openConfirm({
        title: this.t('portTariffLines.confirm.archiveLineTitle'),
        message: this.t('portTariffLines.confirm.archiveLineMessage', {
          concept: line.concept ?? this.t('portTariffLines.confirm.thisLine'),
        }),
      })
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }

        this.loaderService.showLoader();
        this.tariffService.archivePortTariffLine(String(line.id)).pipe(
          finalize(() => this.loaderService.hideLoader())
        ).subscribe({
          next: () => {
            this.showMessage(this.t('portTariffLines.messages.lineArchived'), AlertType.SUCCESS);
            this.reloadData();
          },
          error: (error: HttpErrorResponse) => this.showError(error),
        });
      });
  }

  moveLine(line: PortTariffLineModel, direction: -1 | 1): void {
    if (!this.isEditableTariff() || !this.isActiveLine(line)) {
      return;
    }

    const currentLines = [...this.activeTariffLines()];
    const currentIndex = currentLines.findIndex((item) => String(item.id) === String(line.id));
    const targetIndex = currentIndex + direction;

    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= currentLines.length) {
      return;
    }

    [currentLines[currentIndex], currentLines[targetIndex]] = [currentLines[targetIndex], currentLines[currentIndex]];

    const payload = currentLines.map((item, index) => ({
      id: String(item.id),
      sortOrder: index + 1,
    }));

    this.loaderService.showLoader();
    this.tariffService.reorderPortTariffLines(payload).pipe(
      finalize(() => this.loaderService.hideLoader())
    ).subscribe({
      next: () => this.reloadData(),
      error: (error: HttpErrorResponse) => this.showError(error),
    });
  }

  isActiveTariff(): boolean {
    return this.tariff()?.isActive !== false && !this.tariff()?.archivedAt;
  }

  isEditableTariff(): boolean {
    return this.isActiveTariff();
  }

  isActiveLine(line?: PortTariffLineModel | null): boolean {
    return !!line && line.isActive !== false && !line.archivedAt;
  }

  canMoveLineUp(line: PortTariffLineModel): boolean {
    const activeLines = this.activeTariffLines();
    const index = activeLines.findIndex((item) => String(item.id) === String(line.id));
    return index > 0;
  }

  canMoveLineDown(line: PortTariffLineModel): boolean {
    const activeLines = this.activeTariffLines();
    const index = activeLines.findIndex((item) => String(item.id) === String(line.id));
    return index >= 0 && index < activeLines.length - 1;
  }

  ngOnDestroy(): void {
    this.headerService.clearCustomActions();
    this.headerService.clearHeaderData();
  }

  private updateHeaderConfig(): void {
    const tariff = this.tariff();
    const context = this.context();
    const title = this.headerTitle();
    const metaParts = [
      context.portCode,
      context.country,
      context.terminal,
      tariff?.isActive === false ? this.t('portTariffLines.status.archived') : this.t('portTariffLines.status.active'),
    ].filter(Boolean);

    this.headerService.setHeaderConfig({
      title,
      text: metaParts.join(' · ') || this.headerMeta(),
      visible: true,
      showDefaultFilter: false,
      showDefaultCreate: false,
      customActions: [],
    });
  }

  private activeTariffLines(): PortTariffLineModel[] {
    return this.tariffLines().filter((line) => this.isActiveLine(line));
  }

  private sortedLines(lines: PortTariffLineModel[]): PortTariffLineModel[] {
    return [...lines].sort((left, right) => Number(left.sortOrder ?? 0) - Number(right.sortOrder ?? 0));
  }

  private extractCollection(data: any): any[] {
    if (Array.isArray(data)) {
      return data;
    }

    if (Array.isArray(data?.items)) {
      return data.items;
    }

    if (Array.isArray(data?.data)) {
      return data.data;
    }

    if (Array.isArray(data?.['hydra:member'])) {
      return data['hydra:member'];
    }

    return [];
  }

  private showMessage(message: string, type: AlertType): void {
    this.alertService.show({
      type,
      message,
      duration: 2500,
      inBottom: true,
    });
  }

  private showError(error: HttpErrorResponse): void {
    const payload = error?.error;
    const detail =
      payload?.detail ??
      payload?.error ??
      payload?.message ??
      (Array.isArray(payload?.violations)
        ? payload.violations.map((violation: any) => violation?.message).filter(Boolean).join(' · ')
      : null) ??
      this.t('errors.generic');

    this.showMessage(detail, AlertType.ERROR);
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}
